import { defineStore } from 'pinia';
import { get, last } from 'lodash-es';
import { bus } from '@/lib/eventBus';
import {
  useMessageStore,
} from '@/stores';
import * as types from '@/lib/constants/store';

const useQuicklistStore = defineStore('studio-quicklist', {
  state: () => ({
    quicklist: {},
    quicklistIds: [],
  }),
  actions: {
    async [types.GET_QUICKLIST]() {
      // Get the first page of the QL. This runs when the user
      // opens the profile panel for the first time, so it's only
      // needed in standalone Studio (not the embedded library view).
      const qlRes = await this.deApiFetch('/mycontent/quicklist');
      this.quicklist = {
        quicklist: qlRes.data.quicklist,
        tokens: qlRes.data.tokens,
      };
    },
    async [types.GET_QUICKLIST_IDS](payload = {}) {
      const { allowPublicAccess } = payload;

      try {
        const qlIdsRes = await this.deApiFetch('/mycontent/quicklist/ids', {
          skipErrorHandling: allowPublicAccess || false,
        });
        this.quicklistIds = qlIdsRes.data.quicklist;
      } catch (e) {
        if (allowPublicAccess) {
          // For publicly-accessible boards, we'd expect this to error
          return;
        }
        throw e;
      }
    },
    [types.SET_QUICKLIST_IDS](quicklistIds) {
      this.quicklistIds = quicklistIds;
    },
    async [types.ADD_TO_QUICKLIST](asset) {
      const messageStore = useMessageStore();
      try {
        const res = await this.deApiFetch(`/mycontent/quicklist/add/${asset.id}`, {
          method: 'PUT',
        });

        if (res?.status === 200) {
          messageStore[types.SET_APP_MESSAGE]({
            type: 'QuickListMessage',
            op: 'add',
            assetName: asset.title,
          });
          this.quicklistIds.push(asset.id);
          this.quicklist.quicklist?.unshift(asset);
          bus.emit('quicklist:added', { asset: res?.data?.asset });
        } else {
          throw res;
        }
      } catch (error) {
        messageStore[types.SET_APP_MESSAGE]({
          type: 'QuickListErrorMessage',
          op: 'add',
          message: get(error, 'response.data.meta.message', error),
        });
      }
    },

    async [types.REMOVE_FROM_QUICKLIST](asset) {
      const messageStore = useMessageStore();
      try {
        const res = await this.deApiFetch(`/mycontent/quicklist/remove/${asset.id}`, {
          method: 'DELETE',
        });

        if (res?.status === 200) {
          messageStore[types.SET_APP_MESSAGE]({
            type: 'QuickListMessage',
            op: 'remove',
            assetName: asset.title,
          });

          bus.emit('quicklist:removed', { asset });

          const idsIdx = this.quicklistIds.indexOf(asset.id);
          if (idsIdx === -1) return;

          this.quicklistIds.splice(idsIdx, 1);

          if (this.quicklist.quicklist) {
            const itemIdx = this.quicklist.quicklist.findIndex((a) => a.id === asset.id);
            if (itemIdx === -1) return;

            this.quicklist.quicklist.splice(itemIdx, 1);
          }
        } else {
          throw res;
        }
      } catch (error) {
        messageStore[types.SET_APP_MESSAGE]({
          type: 'QuickListErrorMessage',
          op: 'remove',
          message: get(error, 'response.data.meta.message', error),
        });
      }
    },
    async [types.REMOVE_ALL_FROM_QUICKLIST]() {
      const messageStore = useMessageStore();
      const res = await this.deApiFetch('/mycontent/quicklist/removeall', {
        method: 'DELETE',
      });

      if (res?.status === 200) {
        messageStore[types.SET_APP_MESSAGE]({
          type: 'QuickListDeleteAllMessage',
        });
        this.quicklistIds = [];
        this.quicklist.quicklist = [];
      } else {
        // Errors are handled in the component rather than here
        throw res;
      }
    },
    async [types.MOVE_QUICKLIST_ITEM]({ items, position, itemId }) {
      // This is not currently in use; the quicklist folder id is not accessible yet.
      // STUD-2920 will fix this
      const quicklistFolderId = 'TODO';

      this.quicklist = {
        quicklist: items,
        tokens: this.quicklist.tokens,
      };

      this.deApiFetch(
        `${this.domains.appDomain}/folder/${quicklistFolderId}/asset/${itemId}/position/${position}`,
        {
          method: 'POST',
          body: null,
          headers: {
            'X-Token': this.quicklist.tokens.reposition,
          },
        },
      );
    },
    [types.LOAD_MORE_QUICKLIST]() {
      const lastAssetId = last(this.quicklist.quicklist).id;

      this.deApiFetch(`/mycontent/quicklist?after_id=${lastAssetId}`).then(({ data }) => {
        this.quicklist = {
          quicklist: [...this.quicklist.quicklist, ...data.quicklist],
          tokens: this.quicklist.tokens,
        };
      });
    },
  },
});

export default useQuicklistStore;
